///<amd-module name="typeahead/core/logging/PerformanceMetrics" />
import * as abTest from "typeahead/core/abTest";
import {Lob, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export interface ITypeaheadRequestMetric {
    inputElementId: string;
    query: string;
    timestamp: number;
    triggerType: string;
}

export interface ITypeaheadResponseMetric {
    inputElementId: string;
    latency: number;
    query: string;
    suggestionCount: number;
    timestamp: number;
}

export interface ITypeaheadTriggerMetric {
    request: ITypeaheadRequestMetric;
    response?: ITypeaheadResponseMetric;
}

export interface IAdditionalMetric {
    deviceType: string;
    guid: string;
    lob: string;
    locale: string;
    pageId: string;
    pointOfSale: string;
    siteId: string;
    userAgent: string;
}

export interface IAggregatedPerformanceMetric {
    latency0?: number;
    latency1?: number;
    latency2?: number;
    latency3?: number;
    latency4?: number;
    latency5?: number;
    latency6?: number;
    latency7?: number;
    latency8?: number;
    latency9?: number;
    latencyLast?: number;
    latencyWorst?: number;
    latencyMedian?: number;
    totalTriggers: number;
    totalSuccess: number;
    queryWorst?: string;
    queryLast: string;
    inputElementId: string;
    lob: string;
    variants: string;
    name: string;
}

export class PerformanceMetrics {
    private static getAdditionalMetric(): IAdditionalMetric {
        return {
            deviceType: TypeaheadUtils.getDeviceType(),
            guid: TypeaheadUtils.getGuid(),
            lob: Lob[TypeaheadUtils.getLob()],
            locale: TypeaheadUtils.getLocale(),
            pageId: TypeaheadUtils.getPageId(),
            pointOfSale: TypeaheadUtils.getPointOfSale(),
            siteId: TypeaheadUtils.getSiteId(),
            userAgent: navigator.userAgent,
        };
    }

    private entries: ITypeaheadTriggerMetric[] = [];
    private readonly MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING = 10;
    private readonly METRIC_NAME = "UtaPerformanceMetric";
    private activeEssTests: string;

    public logRequest(triggerType: string, inputElementId: string, query: string): void {
        let request: ITypeaheadRequestMetric = {
            inputElementId,
            query,
            timestamp: Date.now(),
            triggerType,
        };
        this.entries.push({
            request,
        });
    }

    public logResponse(inputElementId: string, query: string, suggestionCount: number, activeEssTests: string): void {
        let entry: ITypeaheadTriggerMetric =
            this.getRequestMetricEntry(inputElementId, query);
        if (entry) {
            let responseTimestamp: number = Date.now();
            entry.response = {
                inputElementId,
                latency: (responseTimestamp - entry.request.timestamp),
                query,
                suggestionCount,
                timestamp: responseTimestamp,
            };
            if (!this.activeEssTests && activeEssTests) {
                this.activeEssTests = activeEssTests;
            }
        }
    }

    public getAggregatedMetric(): IAggregatedPerformanceMetric {
        if (this.entries.length) {
            let metric: IAggregatedPerformanceMetric = <IAggregatedPerformanceMetric> {};

            let entryCount = this.MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING < this.entries.length ?
                this.MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING : this.entries.length;
            for (let i: number = 0; i < entryCount; i++) {
                let entry: ITypeaheadTriggerMetric = this.entries[i];
                if (entry.response) {
                    metric["latency" + i] = entry.response.latency;
                }
            }
            metric.latencyMedian = this.getMedianLatency();

            let worstLatencyMetric: ITypeaheadTriggerMetric = this.getWorstLatencyMetric();
            metric.queryWorst = worstLatencyMetric ? this.getWorstLatencyMetric().response.query : undefined;
            metric.latencyWorst = worstLatencyMetric ? this.getWorstLatencyMetric().response.latency : undefined;

            let last: ITypeaheadTriggerMetric = this.entries[this.entries.length - 1];
            metric.queryLast = last.request.query;
            metric.latencyLast = last.response ? last.response.latency : undefined;

            metric.totalTriggers = this.entries.length;
            metric.totalSuccess = this.getSuccessMetrics().length;

            metric.inputElementId = this.entries[0].request.inputElementId;
            metric.lob = Lob[TypeaheadUtils.getLob()];
            metric.variants =
                _.compact([abTest.getActiveClientTests(), this.activeEssTests]).join("|");
            metric.name = this.METRIC_NAME;
            return metric;
        }
        return undefined;
    }

    public getAllEntries(): Array<ITypeaheadRequestMetric & IAdditionalMetric> |
        Array<ITypeaheadResponseMetric & IAdditionalMetric> {
        let logEntries = [];
        for (let i: number = 0; i < this.entries.length; i++) {
            logEntries.push($.extend(this.entries[i].request, PerformanceMetrics.getAdditionalMetric()));
            if (this.entries[i].response) {
                logEntries.push(
                    $.extend(this.entries[i].response, PerformanceMetrics.getAdditionalMetric()));
            }
        }
        return logEntries;
    }

    private getRequestMetricEntry(inputElementId: string, query: string) {
        for (let i: number = 0; i < this.entries.length; i++) {
            let entry: ITypeaheadTriggerMetric = this.entries[i];
            if (entry.request.inputElementId === inputElementId && entry.request.query === query && !entry.response ) {
                return entry;
            }
        }
        return undefined;
    }

    private getWorstLatencyMetric(): ITypeaheadTriggerMetric {
        let worstLatencyMetric: ITypeaheadTriggerMetric = undefined;
        for (let i: number = 0; i < this.entries.length; i++) {
            let entry: ITypeaheadTriggerMetric = this.entries[i];
            if (entry.response) {
                if (!worstLatencyMetric || worstLatencyMetric.response.latency < entry.response.latency) {
                    worstLatencyMetric = entry;
                }
            }
        }
        return worstLatencyMetric;
    }

    private getMedianLatency(): number {
        let medianLatency: number = undefined;
        let successMetrics: ITypeaheadTriggerMetric[] = this.getSuccessMetrics();
        successMetrics.sort((a: ITypeaheadTriggerMetric, b: ITypeaheadTriggerMetric) => {
            return a.response.latency - b.response.latency;
        });
        if (successMetrics.length) {
            let mid = Math.floor(successMetrics.length / 2);
            medianLatency = successMetrics.length % 2 ? successMetrics[mid].response.latency :
                ((successMetrics[mid - 1].response.latency + successMetrics[mid].response.latency) / 2);
        }
        return medianLatency;
    }

    private getSuccessMetrics(): ITypeaheadTriggerMetric[] {
        return this.entries.filter((entry) => {
            return entry.response;
        });
    }
}
